import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { useDynamicFormsTranslate, useSelectItemsTranslate } from "@ryde/redux";
import { ICustomField, IStyledAttributes } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { FullWidthTextFieldWrapper } from "../parts-styled";

export const RadioGroupStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "radio-group",
})`
  .Mui-checked {
    color: ${rydeColors.steel};
  }
  .MuiFormLabel-root {
    color: ${rydeColors.black};
  }
  .MuiFormGroup-root[role="radiogroup"] {
    padding-inline-start: 20px;
  }
`;

export const RadioGroupTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
  labelTranslated,
}) => {
  const { control } = useFormContext();

  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });

  const { translateLabel } = useSelectItemsTranslate();

  const {
    radioGroupItems,
    selectItems,
    newItemValue,
    skipDropdownItemsTranslation,
  } = formAttributes || {};
  const radioItems = useMemo(() => {
    const items = radioGroupItems || selectItems;

    if (!items?.length) {
      return [];
    }

    return items;
  }, [radioGroupItems, selectItems]);

  return (
    <FullWidthTextFieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({
          field: { value: fieldValue, ...restFieldProps },
          fieldState: { error },
        }) => (
          <RadioGroupStyled>
            <FormControl
              data-e2e-template="radio-group-template"
              data-e2e-field-name={name}
              error={!!error}
            >
              <FormLabel>{formLabelTranslated}</FormLabel>
              <RadioGroup
                {...restFieldProps}
                value={fieldValue || newItemValue}
              >
                {radioItems?.map(radioGroupItem => {
                  return (
                    <FormControlLabel
                      key={String(radioGroupItem.value)}
                      value={radioGroupItem.value}
                      control={<Radio onClick={e => e.stopPropagation()} />}
                      label={
                        skipDropdownItemsTranslation
                          ? radioGroupItem.label
                          : translateLabel(radioGroupItem.label)
                      }
                      defaultChecked={radioGroupItem.value === 1}
                      onClick={e => e.preventDefault()}
                    />
                  );
                })}
              </RadioGroup>
              {error && (
                <FormHelperText data-e2e="radio-group-template-input">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>
          </RadioGroupStyled>
        )}
      />
    </FullWidthTextFieldWrapper>
  );
};
